/**
 * Мои звонки: история и отчёты (T18.8).
 *
 * Отчёт — разбор, а не расшифровка (`02_SPEC.md` §11.1): оценка, торг,
 * ответы, красные флаги, рекомендация. Номера продавца здесь нет ни в
 * каком виде (L-1); объявление открывается ссылкой на оригинал (L-4).
 */

import { useCallback } from 'react';

import { request } from '../../shared/api';
import { openLink } from '../../telegram/webapp';
import { useLoad } from '../../shared/use-load';

interface Report {
  readonly summary: string;
  readonly answers: readonly { key: string; question: string; answer: string | null }[];
  readonly red_flags: readonly string[];
  readonly bargaining: 'yes' | 'no' | 'unknown';
  readonly recommendation: string;
  readonly score: number;
}

interface Call {
  readonly id: string;
  readonly status: string;
  readonly failure_reason: string | null;
  readonly created_at: string;
  readonly listing_url: string | null;
  readonly preset_name: string;
  readonly report: Report | null;
}

const STATUS: Readonly<Record<string, string>> = {
  queued: 'В очереди',
  dialing: 'Звоним',
  ringing: 'Идут гудки',
  connected: 'Разговор',
  in_progress: 'Разговор',
  completed: 'Состоялся',
  failed: 'Не состоялся',
  no_answer: 'Не ответили',
  busy: 'Занято',
  cancelled: 'Отменён',
};

const REASONS: Readonly<Record<string, string>> = {
  no_route: 'нет доступного поставщика связи',
  no_runner: 'нет доступного поставщика связи',
  provider_error: 'поставщик связи не смог соединить',
  bad_preset: 'сценарий повреждён',
  paid_not_supported: 'платные звонки пока не открыты',
};

const BARGAINING: Readonly<Record<string, string>> = {
  yes: 'готов торговаться',
  no: 'без торга',
  unknown: 'о торге не выяснено',
};

export function CallsScreen({ onBack }: { onBack: () => void }): React.JSX.Element {
  const [state, retry] = useLoad<Call[]>(useCallback(() => request<Call[]>('/v1/ai-calls'), []));

  return (
    <>
      <button type="button" className="back" onClick={onBack}>
        ← Профиль
      </button>
      <h1 className="screen__title">Звонки</h1>

      {state.kind === 'loading' ? <p className="notice">Загружаем…</p> : null}
      {state.kind === 'failed' ? (
        <button type="button" className="button" onClick={retry}>
          Не удалось загрузить — повторить
        </button>
      ) : null}
      {state.kind === 'ready' && state.value.length === 0 ? (
        <p className="notice">
          Звонков ещё не было. Позвонить продавцу можно из карточки объявления.
        </p>
      ) : null}

      {state.kind === 'ready'
        ? state.value.map((call) => (
            <div key={call.id} className="card">
              <p>
                <b>{STATUS[call.status] ?? call.status}</b> ·{' '}
                {new Date(call.created_at).toLocaleString('ru-RU')} · {call.preset_name}
              </p>
              {call.failure_reason === null ? null : (
                <p>{REASONS[call.failure_reason] ?? call.failure_reason}</p>
              )}
              {call.report === null ? null : (
                <>
                  <p>
                    Оценка {call.report.score} из 100 · {BARGAINING[call.report.bargaining]}
                  </p>
                  <p>{call.report.summary}</p>
                  <ul>
                    {call.report.answers.map((one) => (
                      <li key={one.key}>
                        {one.question}: {one.answer ?? 'не выяснено'}
                      </li>
                    ))}
                  </ul>
                  {call.report.red_flags.length === 0 ? null : (
                    <p>Насторожило: {call.report.red_flags.join('; ')}</p>
                  )}
                  <p>Рекомендация: {call.report.recommendation}</p>
                </>
              )}
              {call.listing_url === null ? null : (
                <button
                  type="button"
                  className="button"
                  onClick={() => {
                    openLink(call.listing_url ?? '');
                  }}
                >
                  Открыть объявление
                </button>
              )}
            </div>
          ))
        : null}
    </>
  );
}
